import { html, css, nothing, LitElement } from 'lit';
import { property, state, query, customElement } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import {
    updateAccreditationMapping,
    createAccreditationMapping,
    removeAccreditationMapping,
    openAccreditationMapping,
    markAccreditationSaveAttempt,
    saveAccreditationInfo,
} from '../../store/accreditationInfo/accreditationInfo.js';
import { navigateBack } from '../../store/route/route.js';
import { translate } from '../../helpers/translate.js';
import { formatDate, getExpirationStatus } from '../../helpers/controls.js';
import { getInvalidMappingIndexes } from '../../helpers/accreditationValidation.js';
import '../shared/sdb-status-message.js';
import '../shared/sdb-overlay-layout.js';
import '@vaadin/select/theme/material/vaadin-select.js';
import '@vaadin/number-field/theme/material/vaadin-number-field.js';
import '@vaadin/text-field/theme/material/vaadin-text-field.js';
import '@vaadin/form-layout/theme/material/vaadin-form-layout.js';
import '@sdb-develop/learning-frontend';
import '@sdb-develop/learning-frontend/dist/src/sdb-content-date-picker';
import '@material/web/fab/fab.js';

@customElement('sdb-accreditation-info')
class SdbAccreditationInfo extends connect(store)(LitElement) {
    @property({ type: String })
    accessor targetId;

    @state()
    accessor name = '';

    @state()
    accessor mappings = [];

    @state()
    accessor organizations = [];

    @state()
    accessor isDirty = false;

    @state()
    accessor isLoading = false;

    @state()
    accessor status = 'idle';

    @query('#accreditation_form')
    accessor accreditationForm;

    stateChanged(state) {
        this.name = state.accreditationInfo?.catalogItemName;
        this.mappings = state.accreditationInfo?.current;
        this.organizations = state.accreditationInfo?.organizations;
        this.isDirty = state.accreditationInfo?.dirty;
        this.isLoading = state.accreditationInfo?.status === 'loading' || !state.accreditationInfo?.current;
        this.status = state.accreditationInfo?.status;
    }

    willUpdate() {
        this._invalidIndexes = getInvalidMappingIndexes(this.mappings);
        // A mapping added in this session is only marked as invalid after a save attempt.
        this._markedIndexes = this._invalidIndexes.filter((index) => !this.mappings[index].$new);
    }

    static get styles() {
        return [
            sharedStyles,
            css`
                :host {
                    display: block;
                }

                [slot=content] {
                    width: 100%;
                    padding: 0 20px;
                    max-width: 1400px;
                }

                .actions {
                    display: flex;
                    margin-top: 5px;
                }

                .actions sdb-content-button {
                    margin-left: auto;
                }

                [slot=content] {
                    width: 100%;
                }

                vaadin-number-field.small {
                    width: 100px !important;
                }

                .invalid-mappings {
                    color: var(--alert-color);
                    background: var(--alert-background);
                    border: 1px solid var(--alert-color);
                    border-radius: 0 10px 10px 10px;
                    padding: 5px 10px;
                    margin-bottom: 5px;
                }

                .invalid-mappings button {
                    background: none;
                    border: none;
                    padding: 0 5px 0 0;
                    color: inherit;
                    font: inherit;
                    text-decoration: underline;
                    cursor: pointer;
                }
            `,
        ];
    }

    render() {
        if (this.isLoading) {
            return nothing;
        }

        return html`
            <sdb-overlay-layout
                headline=${this.name}
                ?dirty=${this.isDirty}
                @back=${() => { store.dispatch(navigateBack('compositions')); }}>

                <div slot="buttons">
                    <sdb-content-button
                        label=${translate('save')}
                        ?disabled=${!this.isDirty || this.status === 'saving'}
                        @click=${() => { this._save(); }}>
                    </sdb-content-button>
                </div>

                <div slot="content">

                    ${this._renderInvalidMappings()}

                    ${this.mappings.length === 0 ? html`
                        <sdb-status-message label=${translate('noAccreditationInfoFound')}>
                        </sdb-status-message>
                    ` : html`
                        <sdb-layout-100>
                            <iron-form id="accreditation_form">
                                <form>
                                    <sdb-content-accordion separator>

                                        <sdb-content-accordionheading class="hideonmobile">
                                            <sdb-content-headingitem
                                                textDark
                                                slot="1"
                                                icon="fal fa-users"
                                                headline=${translate('pe.organization')}>
                                            </sdb-content-headingitem>
                                            <sdb-content-headingitem
                                                textDark
                                                slot="2"
                                                icon="fal fa-calendar"
                                                headline=${translate('pe.duration')}>
                                            </sdb-content-headingitem>
                                            <sdb-content-headingitem
                                                textDark
                                                slot="3"
                                                icon="fal fa-input-numeric"
                                                headline=${translate('pe.courseId')}>
                                            </sdb-content-headingitem>
                                            <sdb-content-headingitem
                                                textDark
                                                slot="4"
                                                icon="fal fa-input-numeric"
                                                headline=${translate('pe.moduleId')}>
                                            </sdb-content-headingitem>
                                        </sdb-content-accordionheading>

                                        ${this.mappings.map((mapping, index) => html`
                                            <sdb-content-accordionitem
                                                data-index=${index}
                                                headline=${this._getOrganizationLabel(mapping)}
                                                statusColor=${this._markedIndexes.includes(index) ? 'red' : this._getWarningColor(mapping.validTill)}
                                                @opened-changed=${(e) => {
                                                    if (e.detail.value !== mapping.$opened) {
                                                        store.dispatch(openAccreditationMapping({ index, value: e.detail.value }));
                                                    }
                                                }}
                                                ?opened=${mapping.$opened}>

                                                ${this._markedIndexes.includes(index) ? html`
                                                    <sdb-content-chipbar slot="chipbar">
                                                        <sdb-content-chip
                                                            light
                                                            color="var(--alert-color)"
                                                            label=${translate('accreditationIncomplete')}>
                                                        </sdb-content-chip>
                                                    </sdb-content-chipbar>
                                                ` : nothing}

                                                <div slot="2">${formatDate(mapping.validFrom)} - ${formatDate(mapping.validTill)}</div>
                                                <div slot="3">${mapping.data.peCourseId}</div>
                                                <div slot="4">${mapping.data.peModuleId}</div>

                                                ${this._renderMappingForm(mapping, index)}

                                                <div class="actions">
                                                    <sdb-content-button
                                                        class="remove-btn"
                                                        label=${translate('remove')}
                                                        @click=${() => { this._remove(index); }}>
                                                    </sdb-content-button>
                                                </div>

                                            </sdb-content-accordionitem>
                                        `)}

                                    </sdb-content-accordion>
                                </form>
                            </iron-form>

                        </sdb-layout-100>
                    `}

                    <md-fab @click=${() => { store.dispatch(createAccreditationMapping()); }}>
                        <sdb-content-icon slot="icon" icon="fal fa-plus" size="24"></sdb-content-icon>
                    </md-fab>
                </div>
            </sdb-overlay-layout>
        `;
    }

    _getOrganizationLabel(mapping) {
        return this.organizations.find((o) => o.value === mapping.organizationKey)?.label ?? mapping.organizationKey;
    }

    _renderInvalidMappings() {
        if (!this._markedIndexes?.length) { return nothing; }

        return html`
            <sdb-layout-100>
                <div class="invalid-mappings" role="alert">
                    ${translate('invalidAccreditationMappings', { count: this._markedIndexes.length })}
                    ${this._markedIndexes.map((index) => html`
                        <button type="button" @click=${() => { this._showInvalidMappings([index]); }}>
                            ${this._getOrganizationLabel(this.mappings[index]) || translate('pe.organization')}
                        </button>
                    `)}
                </div>
            </sdb-layout-100>
        `;
    }

    _renderMappingForm(mapping, index) {
        const update = (changes) => store.dispatch(updateAccreditationMapping({ index, changes }));

        return html`
            <vaadin-form-layout>

                <vaadin-select
                    required
                    transparent
                    label=${translate('pe.organization')}
                    value=${mapping.organizationKey}
                    .items=${this.organizations}
                    @change=${(e) => { update({ organizationKey: e.target.value }); }}
                ></vaadin-select>

                <div></div>

                <sdb-content-date-picker
                    label=${translate('pe.duration.start')}
                    max=${mapping.validTill}
                    value=${mapping.validFrom}
                    @change=${(e) => { update({ validFrom: e.detail.value }); }}
                ></sdb-content-date-picker>

                <sdb-content-date-picker
                    label=${translate('pe.duration.end')}
                    min=${mapping.validFrom}
                    value=${mapping.validTill}
                    @change=${(e) => { update({ validTill: e.detail.value }); }}
                ></sdb-content-date-picker>

                <vaadin-number-field
                    required
                    label=${translate('pe.courseId')}
                    value=${mapping.data.peCourseId}
                    @change=${(e) => { update({ data: { peCourseId: e.target.value } }); }}>
                </vaadin-number-field>

                <vaadin-number-field
                    label=${translate('pe.moduleId')}
                    value=${mapping.data.peModuleId}
                    @change=${(e) => { update({ data: { peModuleId: e.target.value } }); }}>
                </vaadin-number-field>

                <vaadin-number-field
                    class="small"
                    step="0.5"
                    min="0"
                    step-buttons-visible
                    label=${translate('pe.points')}
                    value=${mapping.points}
                    @change=${(e) => { update({ points: e.target.value }); }}>
                </vaadin-number-field>

            </vaadin-form-layout>
        `;
    }

    _getWarningColor(date) {
        switch (getExpirationStatus(date, 3, 'months')) {
        case 'expired': return 'red';
        case 'almost-expired': return 'orange';
        default: return '';
        }
    }

    _remove(index) {
        store.dispatch(removeAccreditationMapping({ index }));
    };

    _save() {
        if (this._invalidIndexes.length > 0) {
            store.dispatch(markAccreditationSaveAttempt());
            this._showInvalidMappings(this._invalidIndexes);

            return;
        }

        if (!this.accreditationForm || this.accreditationForm.validate()) {
            store.dispatch(saveAccreditationInfo({ targetId: this.targetId }));
        } else {
            this.accreditationForm.querySelector('[invalid]')?.scrollIntoView({ block: 'center' });
        }
    };

    async _showInvalidMappings(indexes) {
        indexes
            .filter((index) => !this.mappings[index].$opened)
            .forEach((index) => store.dispatch(openAccreditationMapping({ index, value: true })));
        await this.updateComplete;

        const items = indexes.map((index) => this.renderRoot.querySelector(`sdb-content-accordionitem[data-index="${index}"]`));

        items[0]?.scrollIntoView({ block: 'start' });
        // Validate only the fields of these mappings, so they show what is wrong; an empty new mapping elsewhere stays unmarked.
        items.forEach((item) => item
            ?.querySelectorAll('vaadin-select, vaadin-number-field, sdb-content-date-picker')
            .forEach((field) => field.validate()));
    }
}
